//temp for testing, remove
import { useEffect, useState, type CSSProperties } from "react";
import { useDataContext } from "../../DataProvider/DataContext";
import { CommonButton } from "../../styledComponents/MUI.styles";
import palette from "../../../utilities/palette";
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';

import { 
    blockHeight, 
    blockWidth, 
    addRmvAllBtnSize, 
    addRmvAllBtnMargin, 
    addRmvBtnSize,
    clusterLabelPadding,
    clusterLabelWidth,
    scrollGap,
    clusterLabelTextWidth,
    fullWidth
} from "./sizes"
import { Box, TextField, Tooltip } from "@mui/material";
import type { ClusterSelectClusterInterface } from "../../../models/ClusterSelectInterfaces";
import { FlexRowDiv } from "../../styledComponents/Components.styles";



const ClusterSelectBox = (
    {machineIndex, variantEntry, variantIndex}
: {
    machineIndex: number,
    variantEntry: any,
    variantIndex: number,
}) => {
    
    const { clusterSelectObj, setClusterSelectObj, importProcessData } = useDataContext();


    const [removedImportedClustersPerVariant, setRemovedImportedClustersPerVariant] = useState<Record<string, string[]>>({});



    function calculateRemovedImportedClusters() {
        if(!importProcessData.enabled) {return;}
        
        const removedClustersPerVariant : Record<string, string[]> = {};
        const variant = clusterSelectObj[machineIndex].variantList[variantIndex];
        const variantName = variant.label;
        importProcessData.unRegisteredClusters.forEach((cluster: any) => {
            if(cluster.variantName === variantName){
                if(!(variantName in removedClustersPerVariant)){
                    removedClustersPerVariant[variantName] = [];
                }
                removedClustersPerVariant[variantName].push(cluster.clusterName);
            }
        });
        setRemovedImportedClustersPerVariant(removedClustersPerVariant);
    }


    useEffect(() => {
        calculateRemovedImportedClusters();
    }, [importProcessData]);

    useEffect(() => {
        console.log("Removed imported clusters per variant:", removedImportedClustersPerVariant);
    }, [removedImportedClustersPerVariant]);
    

   
    function addCluster(machineIndex: number, variantIndex: number, clusterIndex: number){

        // console.log("adding cluster: " + `${machineIndex}-${variantIndex}-${clusterIndex}`)
        console.log(`adding cluster: ${machineIndex}-${variantIndex}-${clusterIndex}`)

        setClusterSelectObj((prevState) => {
            const variant = prevState[machineIndex].variantList[variantIndex];

            //should be pointer to the same object, so changes should reflect.
            const cluster = variant.notAddedClusters.splice(clusterIndex, 1)[0];

            variant.addedClusters.push(cluster);


            //if its pending removal, undo that, otherwise make it pending add
            if(variant.pendingRemove.includes(cluster)){
                const index = variant.pendingRemove.indexOf(cluster);
                variant.pendingRemove.splice(index, 1);
            }else{
                variant.pendingAdd.push(cluster);
            }


            return [...prevState]
        })
    }

    function removeCluster(machineIndex: number, variantIndex: number, clusterIndex: number){

        console.log(`removing cluster: ${machineIndex}-${variantIndex}-${clusterIndex}`)

        setClusterSelectObj((prevState) => {
            const variant = prevState[machineIndex].variantList[variantIndex];

            const cluster = variant.addedClusters.splice(clusterIndex, 1)[0];

            variant.notAddedClusters.push(cluster);


            //if its pending removal, undo that, otherwise make it pending add
            if(variant.pendingAdd.includes(cluster)){
                const index = variant.pendingAdd.indexOf(cluster);
                variant.pendingAdd.splice(index, 1);
            }else{
                variant.pendingRemove.push(cluster);
            }


            return [...prevState]
        })
    }



    const [searchText, setSearchText] = useState("");

    //this is a really naive way to implement search, probably a better way
    function search(toSearch: string){
        if(searchText === ""){return true};
        return toSearch.toLowerCase().includes(searchText.toLowerCase());
    }

    function searchInput(e: any){
        setSearchText(e.target.value);
    }

    function addAll(machineIndex: number, variantIndex: number){
        const variant = clusterSelectObj[machineIndex].variantList[variantIndex];

        //can't just call addCluster repeatedly, will do a state change for each cluster, that's too much.


        //reverses the array to keep the ordering the same when moving all values over
            //this avoids making the transfer n^2 by using 'shift' which would re-index the array every time
        const tempArray: ClusterSelectClusterInterface[] = [] 
        for(let i = variant.notAddedClusters.length-1; i >= 0; i--){
            const cluster = variant.notAddedClusters.pop();
            if (cluster) tempArray.push(cluster);
        }

        //need to count down so that it doesn't end halfway due to the length shrinking with 'pop'
        for(let i = tempArray.length-1; i >= 0; i--){
            const cluster = tempArray.pop();
            if (cluster ) {
                variant.addedClusters.push(cluster);
        
                if(variant.pendingRemove.includes(cluster)){
                    const index = variant.pendingRemove.indexOf(cluster);
                    variant.pendingRemove.splice(index, 1);
                }else{
                    variant.pendingAdd.push(cluster);
                }
            }
        }

        setClusterSelectObj([...clusterSelectObj])
    }

    function removeAll(machineIndex: number, variantIndex: number){
        const variant = clusterSelectObj[machineIndex].variantList[variantIndex];

        //reverses the array to keep the ordering the same when moving all values over
            //this avoids making the transfer n^2 by using 'shift' which would re-index the array every time
        const tempArray = []
        for(let i = variant.addedClusters.length-1; i >= 0; i--){
            tempArray.push(variant.addedClusters.pop());
        }

        //need to count down so that it doesn't end halfway due to the length shrinking with 'pop'
        for(let i = tempArray.length-1; i >= 0; i--){
            const cluster = tempArray.pop();

            if (cluster) {
                variant.notAddedClusters.push(cluster);

                if(variant.pendingAdd.includes(cluster)){
                    const index = variant.pendingAdd.indexOf(cluster);
                    variant.pendingAdd.splice(index, 1);
                }else{
                    variant.pendingRemove.push(cluster);
                }
            }
        }

        setClusterSelectObj([...clusterSelectObj])
    }

    

    const style: Record<string, CSSProperties> = {
        variantBlock:{
            minHeight:"200px",
            display:"flex",
            flexDirection:"row",
        },
        clusterLabel:{
            backgroundColor:palette.primary.main,
            color: palette.primary.text,
            width: `${clusterLabelWidth}px`, 
            padding:`5px ${clusterLabelPadding}px`,
            display:"inline-block",
            marginBottom:"5px",
            marginTop:"5px",
            borderRadius:"5px",
        },
        clusterLabelText:{
            display:"inline-block",
            textWrap: "nowrap",
            overflowX:"clip",
            width:`${clusterLabelTextWidth}px`
        },
        addRemoveButton:{
            height:`${addRmvBtnSize}px`, 
            width:`${addRmvBtnSize}px`,
            marginRight:`${scrollGap}px`,
            float:"right"
        },
        selectionBlock:{
            display:"flex",
            flexDirection:"column",
            verticalAlign:"top",
            overflowY:"scroll",
            width: `${blockWidth}px`,
            height: `310px`,
        },
        borderBlock: {
            padding: '20px',
            border: `2px solid #ced4da`,
            borderRadius: '10px',
            marginBottom: '10px',
        },
        middleButtonsContainer:{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
        },
        addRemoveAllButton:{
            height:`${addRmvAllBtnSize}px`,
            width:`${addRmvAllBtnSize}px`, 
            margin:`${addRmvAllBtnMargin}px`
        },
        variantHeader:{
            backgroundColor:"lightSlateGrey",
            border:"2px solid black",
            display:"inline-block",
            width:`${fullWidth}px`,
            padding:"10px",
            color:"black"
        },
        variantBorder:{
            padding:"20px 0px",
            alignSelf: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
        },

        centerAlignedLabel:{
            color: palette.main,
            alignSelf:"center",
            marginBottom:"10px",
            fontWeight:"600",
        },


        cardContainer: {
            marginTop: "10px",
            marginBottom: "10px",
        },

        middleButtonInnerContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: `${blockHeight}px`,
        }
    }


    //might remove variant border depending on how filter restyling turns out
    return(
    
    <div style={style.variantBorder}>

        <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
            <SearchIcon sx={{ color: palette.main, mr: 1, my: 0.5 }} />
            <TextField id="searchBox" type="search" label="Filter" variant="standard"  onChange={(e)=>searchInput(e)}/>
        </Box>



        {variantEntry.notAddedClusters.length === 0 && variantEntry.addedClusters.length === 0 && variantEntry.configs.length === 0 ? 
            <div style={{display: 'flex', flexDirection:'column', alignItems: 'center', fontSize: '14px', color: 'grey'}}>No clusters available</div>
            :
            <div style={style.variantBlock}>

                <div
                    style= {{display: 'flex', flexDirection: 'column', alignItems: 'center'}}
                >
                    <div style={style.centerAlignedLabel}>Available Software Clusters</div>
                <div style={style.borderBlock}> 

                <div style={style.selectionBlock}>
                        {variantEntry.notAddedClusters.map((cluster: ClusterSelectClusterInterface, clusterIndex: number) => {
                            if(search(cluster.label)){return(
                                <CommonButton
                                    key={`unadded-${cluster.label}-${clusterIndex}`}
                                    sx={{margin: '5px'}}
                                    onClick={()=>addCluster(machineIndex, variantIndex, clusterIndex)}
                                >
                                    <Tooltip title={cluster.label} placement="left-start" >

                                        <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: `100%`, fontSize: '14px'}}>
                                            <div style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                textAlign: 'left',
                                                fontSize: '12px'
                                            }}>
                                                {cluster.label}
                                            </div>
                                            <AddIcon/>
                                        </div>
                                    </Tooltip>



                                </CommonButton>
                            )}
                            return null;
                })}
                </div>   
                </div>             
                </div>



                <div style={style.middleButtonsContainer}>

                        <CommonButton
                            sx={style.addRemoveAllButton}
                            onClick={()=>addAll(machineIndex, variantIndex)}
                        >
                            {">>"}
                        </CommonButton>
                        <CommonButton
                            sx={style.addRemoveAllButton}
                            onClick={()=>removeAll(machineIndex, variantIndex)}
                        >
                            {"<<"}
                        </CommonButton>


                </div>

                <div
                    style= {{display: 'flex', flexDirection: 'column', alignItems: 'center'}}
                >
                    <div style={style.centerAlignedLabel}>My Software Clusters</div>
                    <div style={style.borderBlock}> 
                        <div style={style.selectionBlock}>
                                {variantEntry.addedClusters.map((cluster: ClusterSelectClusterInterface, clusterIndex: number) => {
                                    if(search(cluster.label)){return(
                                        <CommonButton
                                            key={`unadded-${cluster.label}-${clusterIndex}`}
                                            sx={{margin: '5px'}}
                                            onClick={()=>removeCluster(machineIndex, variantIndex, clusterIndex)}
                                        >
                                            <Tooltip title={cluster.label} placement="left-start" >

                                                <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: `100%`, fontSize: '14px'}}>
                                                    <div style={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        textAlign: 'left',
                                                        fontSize: '12px'
                                                    }}>
                                                        {cluster.label}
                                                    </div>
                                                    <AddIcon/>
                                                </div>
                                            </Tooltip>



                                        </CommonButton>
                                    )}
                                    return null;
                        })}
                        </div>   
                    </div>

                    {/* Warning: removed unregistered clusters */}
                    {importProcessData.enabled && removedImportedClustersPerVariant[variantEntry.label] && removedImportedClustersPerVariant[variantEntry.label].length > 0 &&
                    <>
                        <FlexRowDiv style={{alignItems: 'center', gap: '10px', marginTop: '10px'}}>
                            <Box sx={{ 
                                position: 'relative', 
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <Box sx={{
                                    position: 'absolute',
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    backgroundColor: 'black',
                                    zIndex: 0,
                                    top: '40%',
                                }}/>
                                <WarningIcon sx={{color: 'orange', position: 'relative', zIndex: 1}}/>
                            </Box>
                            <span>SRP Import Warning</span>
                        </FlexRowDiv>
                       { removedImportedClustersPerVariant[variantEntry.label].map((clusterName: string, index: number) => (
                            <div 
                                key={`removed-warning-${clusterName}-${index}`}
                                style={{
                                    fontSize: '12px',
                                    marginTop: '5px',
                                    textAlign: 'start',
                                }}
                            >
                                - Removed unregistered cluster from import: <b>{clusterName}</b>
                            </div>
                        ))}
                    </>
                    }

                    
                    
                    
                </div>

            </div>
        }



    </div>);
}

export default ClusterSelectBox;